export interface ServiceFAQ {
  q: string;
  a: string;
}

export interface RelatedService {
  name: string;
  slug: string;
}

export interface HowWeFixStep {
  title: string;
  description: string;
}

export interface InternalLinkBlurb {
  before: string;
  linkText: string;
  slug: string;
  after: string;
}

export interface ServicePageData {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  heroImageUrl: string;
  tagline: string;
  intro: string[];
  internalLinkBlurbs: InternalLinkBlurb[];
  signs: string[];
  howWeFixIt: HowWeFixStep[];
  benefits: string[];
  faqs: ServiceFAQ[];
  relatedServices: RelatedService[];
  icon: string;
}

export const servicePages: ServicePageData[] = [
  {
    slug: "pool-leak-detection-las-vegas",
    title: "Pool Leak Detection in Las Vegas — Find & Fix It Fast",
    metaTitle: "Pool Leak Detection Las Vegas — Same-Day Service | Leak Detectives",
    metaDescription: "Expert pool leak detection in Las Vegas. Licensed & insured specialists use non-invasive methods to find pool leaks fast. Same-day service. Call (725) 267-5482.",
    heroImageUrl: "https://public.readdy.ai/ai/img_res/6e09803628e21fa6607ca7a0f5323855.jpg",
    tagline: "Stop Losing Water — Find Your Pool Leak Today",
    intro: [
      "A leaking pool is more than just an annoyance — it's a costly problem that gets worse every single day it goes undetected. In Las Vegas, where water is precious and expensive, a pool leak can add hundreds of dollars to your monthly water bill while silently eroding the ground beneath your deck.",
      "At Leak Detectives, we specialize in professional pool leak detection throughout Las Vegas and Clark County. Using advanced electronic listening equipment, pressure testing, and dye testing, we pinpoint the exact location of your pool leak — without digging up your yard or dismantling your pool deck.",
      "Whether you're in Henderson, Summerlin, North Las Vegas, Spring Valley, or Paradise, our licensed and insured specialists will give you a fast, accurate diagnosis with flat-rate pricing and no hidden fees. We serve the entire greater Las Vegas area with same-day appointments."
    ],
    internalLinkBlurbs: [
      {
        before: "If you suspect the leak is coming from pipes near your home's foundation, also read about our",
        linkText: "slab leak repair service",
        slug: "slab-leak-repair-las-vegas",
        after: "— slab and pool plumbing often share the same underground runs."
      },
      {
        before: "Pool leaks left unaddressed can cause serious property damage — learn how our",
        linkText: "water damage restoration team",
        slug: "water-damage-restoration-las-vegas",
        after: "can help if moisture has already reached your home's structure."
      },
      {
        before: "For leaks that extend beyond the pool into your home's supply lines, our",
        linkText: "water leak detection specialists",
        slug: "water-leak-detection-las-vegas",
        after: "use the same non-invasive methods to trace leaks anywhere on your property."
      }
    ],
    signs: [
      "Your pool loses more than 1/4 inch of water per day (beyond normal evaporation)",
      "Water bills have spiked without an obvious explanation",
      "Soggy or sinking ground around the pool deck or equipment",
      "Cracks appearing in the pool deck or surrounding concrete",
      "Auto-fill valve runs constantly to maintain water level",
      "Air bubbles visible in return lines when pump is running",
      "Pool equipment losing prime frequently",
      "Algae growth that keeps returning even after chemical treatment"
    ],
    howWeFixIt: [
      {
        title: "Evaporation Test",
        description: "We start with a bucket evaporation test to determine if you actually have a leak or if your pool is losing water through normal evaporation — saving you from unnecessary repairs."
      },
      {
        title: "Pressure Testing",
        description: "We pressure-test all plumbing lines — suction lines, return lines, and the main drain — to isolate which line is compromised and exactly where the break is located."
      },
      {
        title: "Dye Testing",
        description: "Using specialized dye, we test fittings, lights, skimmers, and the shell itself to identify cracks or seal failures that may not be visible to the naked eye."
      },
      {
        title: "Electronic Leak Detection",
        description: "Our electronic listening equipment can detect the sound of water escaping underground lines — pinpointing the leak location within inches without excavation."
      }
    ],
    benefits: [
      "Licensed & insured pool leak detection specialists",
      "Non-invasive methods — no unnecessary digging or damage",
      "Same-day service throughout Las Vegas and Clark County",
      "Flat-rate pricing — you know the cost before we start",
      "Honest diagnosis — we won't recommend repairs you don't need",
      "We work alongside your pool company or repair contractor"
    ],
    faqs: [
      {
        q: "How do I know if my pool is leaking or just evaporating?",
        a: "A pool in Las Vegas can evaporate 1/4 inch per day or more due to heat and sun. If you're losing more than that — especially overnight — it's likely a leak. We use a bucket test to confirm before any work begins."
      },
      {
        q: "Will you have to drain my pool to find the leak?",
        a: "Not necessarily. Most pool leaks can be located with the pool at normal water level using pressure testing and dye. Draining is rarely required and only done when specifically needed."
      },
      {
        q: "Can you find a leak in my underground pool pipes?",
        a: "Yes — our electronic listening equipment is specifically designed to locate underground pipe leaks without excavation. We can pinpoint a leak within inches."
      },
      {
        q: "Do you repair pool leaks too?",
        a: "We specialize in detection. For repairs, we can coordinate with your pool contractor or recommend trusted local repair professionals."
      },
      {
        q: "How much does pool leak detection cost?",
        a: "We use flat-rate pricing, so your cost is set before we begin. Call (725) 267-5482 for a free, no-obligation estimate."
      }
    ],
    relatedServices: [
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" },
      { name: "Slab Leak Repair", slug: "slab-leak-repair-las-vegas" },
      { name: "Water Damage Restoration", slug: "water-damage-restoration-las-vegas" }
    ],
    icon: "ri-water-fill"
  },
  {
    slug: "water-leak-detection-las-vegas",
    title: "Water Leak Detection in Las Vegas — Accurate, Fast & Non-Invasive",
    metaTitle: "Water Leak Detection Las Vegas — Same-Day | Leak Detectives",
    metaDescription: "Professional water leak detection in Las Vegas. Find hidden leaks fast with non-invasive methods. Licensed & insured. Same-day service. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20water%20leak%20detection%20specialist%20using%20electronic%20acoustic%20listening%20device%20against%20a%20residential%20home%20wall%2C%20detecting%20hidden%20pipe%20leaks%2C%20professional%20uniform%2C%20focused%20expression%2C%20modern%20suburban%20home%20interior%20with%20pipe%20infrastructure%20visible%2C%20bright%20professional%20lighting%2C%20clean%20workspace&width=1920&height=700&seq=water-leak-hero-001&orientation=landscape",
    tagline: "Find Hidden Leaks Before They Destroy Your Home",
    intro: [
      "Hidden water leaks are one of the most destructive and expensive problems a homeowner can face. By the time you see the signs — water stains, peeling paint, a spike in your water bill — the damage has often been building for weeks or months.",
      "Leak Detectives uses professional-grade electronic listening equipment, thermal imaging, and pressure testing to find hidden water leaks throughout your home without cutting into walls, tearing up floors, or destroying your property. We locate the source accurately on the first visit.",
      "Our water leak detection specialists serve all of Las Vegas, Henderson, North Las Vegas, Summerlin, Spring Valley, Paradise, and surrounding Clark County communities. With same-day appointments, flat-rate pricing, and a commitment to non-invasive methods, we give you a fast, accurate answer — so you can stop the damage before it gets worse."
    ],
    internalLinkBlurbs: [
      {
        before: "If your leak is below the concrete foundation, you likely need specialized",
        linkText: "slab leak detection and repair",
        slug: "slab-leak-repair-las-vegas",
        after: "— a common issue in Las Vegas homes with aging copper pipe systems."
      },
      {
        before: "When a leak has caused structural moisture or standing water, our",
        linkText: "water damage restoration service",
        slug: "water-damage-restoration-las-vegas",
        after: "can dry out your home quickly and prevent mold from taking hold."
      },
      {
        before: "Recurring leaks in multiple areas of your home may indicate a need for",
        linkText: "full home repiping",
        slug: "home-repiping-las-vegas",
        after: "— replacing all aging supply lines with modern, durable materials."
      }
    ],
    signs: [
      "Water bill is higher than usual with no explanation",
      "Sound of running water when all fixtures are turned off",
      "Damp spots, water stains, or bubbling paint on walls or ceilings",
      "Musty odor or visible mold in areas not prone to moisture",
      "Warm spots on floors (can indicate a hot water line leak)",
      "Cracks in foundation or walls developing over time",
      "Reduced water pressure throughout the home",
      "Meter continues to move when all water is shut off"
    ],
    howWeFixIt: [
      {
        title: "Meter Test",
        description: "We start by shutting off all water and checking if your meter is still moving — a simple but definitive confirmation that a leak exists somewhere in your system."
      },
      {
        title: "Pressure Testing",
        description: "We isolate your plumbing system into zones and pressure test each one to identify which section is losing pressure, narrowing down the leak location quickly."
      },
      {
        title: "Electronic Acoustic Detection",
        description: "Using ground microphones and acoustic amplifiers, we listen for the specific frequencies produced by pressurized water escaping a pipe — accurate to within inches."
      },
      {
        title: "Thermal Imaging",
        description: "When leaks are behind walls or under slabs, thermal imaging detects temperature differences caused by water movement — revealing the leak without any destruction."
      }
    ],
    benefits: [
      "No unnecessary cutting, digging, or damage to your property",
      "Same-day appointments available throughout Las Vegas",
      "Licensed & insured — certified leak detection specialists",
      "Flat-rate pricing with free, no-obligation estimate",
      "Accurate results on the first visit — no guesswork",
      "We assist with documentation for insurance claims"
    ],
    faqs: [
      {
        q: "Can you find a leak without tearing up my walls?",
        a: "Yes — that's our specialty. We use acoustic listening equipment and thermal imaging to locate leaks without destructive access in most cases."
      },
      {
        q: "My water bill went up but I can't see any leak. What should I do?",
        a: "A higher water bill with no visible source is a classic sign of a hidden leak. Call us — we'll find it fast using professional detection equipment."
      },
      {
        q: "How accurate is your leak detection?",
        a: "Our electronic detection methods are accurate to within a few inches in most cases, allowing repairs to be made with minimal access and no guesswork."
      },
      {
        q: "Do you also repair the leak once you find it?",
        a: "Yes — in many cases we can repair the leak the same day. Depending on the repair needed, we'll give you a clear cost before any work begins."
      },
      {
        q: "Is water leak detection covered by insurance?",
        a: "Many homeowner's insurance policies cover water damage caused by sudden leaks. We can provide documentation to help with your claim."
      }
    ],
    relatedServices: [
      { name: "Slab Leak Repair", slug: "slab-leak-repair-las-vegas" },
      { name: "Pool Leak Detection", slug: "pool-leak-detection-las-vegas" },
      { name: "Water Damage Restoration", slug: "water-damage-restoration-las-vegas" },
      { name: "Home Repiping", slug: "home-repiping-las-vegas" }
    ],
    icon: "ri-drop-fill"
  },
  {
    slug: "home-repiping-las-vegas",
    title: "Home Repiping in Las Vegas — Complete Repipe Services",
    metaTitle: "Home Repiping Las Vegas — Licensed & Insured | Leak Detectives",
    metaDescription: "Complete home repiping in Las Vegas. Replace outdated or failing pipes with modern materials. Licensed & insured. Same-day assessment. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20licensed%20plumber%20installing%20new%20copper%20or%20PEX%20pipe%20system%20in%20a%20residential%20home%20during%20repiping%20project%2C%20crawl%20space%20and%20wall%20access%20visible%2C%20modern%20plumbing%20materials%2C%20professional%20tools%2C%20bright%20work%20lighting%20in%20a%20Las%20Vegas%20suburban%20home%20with%20clean%20workspace%20and%20organized%20installation&width=1920&height=700&seq=repipe-hero-001&orientation=landscape",
    tagline: "New Pipes. Better Water. No More Leaks.",
    intro: [
      "If your home has aging galvanized steel pipes, corroded copper, or polybutylene plumbing, you may be living with a ticking time bomb. Old pipes deteriorate from the inside out — reducing water pressure, discoloring your water, and failing at the worst possible times.",
      "Leak Detectives provides complete home repiping services throughout Las Vegas, Henderson, Summerlin, North Las Vegas, Spring Valley, Paradise, and surrounding Clark County communities. We replace your entire plumbing system with modern, durable PEX or copper pipes that are built to last decades.",
      "Whether you've had repeated leaks, noticed rust-colored water, or want to proactively protect your home, a full repipe is often the most cost-effective long-term solution — and we make the process straightforward with flat-rate pricing and a free, no-obligation assessment."
    ],
    internalLinkBlurbs: [
      {
        before: "Before committing to a full repipe, our team can perform a",
        linkText: "professional water leak detection",
        slug: "water-leak-detection-las-vegas",
        after: "to determine whether isolated repairs could solve the problem first."
      },
      {
        before: "If your main supply line from the street is also failing, see our",
        linkText: "mainline replacement service",
        slug: "mainline-replacement-las-vegas",
        after: "— often done in combination with a whole-home repipe for complete peace of mind."
      },
      {
        before: "Las Vegas hard water is a major contributor to pipe corrosion — consider pairing your repipe with our",
        linkText: "water filtration and softener installation",
        slug: "water-filtration-softener-las-vegas",
        after: "to protect your new pipes for decades to come."
      }
    ],
    signs: [
      "Multiple leaks in different areas of the home within a short period",
      "Rust-colored, brown, or discolored water from your taps",
      "Low water pressure that doesn't improve with fixture replacement",
      "Pipes are made of galvanized steel or polybutylene (recalled material)",
      "Home is 30+ years old and has never had plumbing updated",
      "Persistent metallic taste or odor in drinking water",
      "Visible corrosion or discoloration on exposed pipes",
      "Frequent pinhole leaks in copper pipes"
    ],
    howWeFixIt: [
      {
        title: "Full Plumbing Assessment",
        description: "We inspect your current pipe material, condition, and layout — identifying problem areas and giving you a complete picture before recommending a repipe."
      },
      {
        title: "Minimal-Access Installation",
        description: "Our team uses strategic access points to run new PEX or copper pipes throughout your home with minimal drywall cutting — reducing repair costs and mess."
      },
      {
        title: "Complete System Replacement",
        description: "We replace all supply lines — hot and cold — from the main to every fixture. This eliminates the possibility of old pipe segments causing future problems."
      },
      {
        title: "Inspection & Final Walkthrough",
        description: "After installation, we pressure test the entire new system, restore access points, and walk you through everything before we consider the job complete."
      }
    ],
    benefits: [
      "Licensed & insured — complete residential repiping specialists",
      "Modern PEX or copper pipe options with long-term warranties",
      "Minimal disruption — strategic access points, not wholesale demolition",
      "Flat-rate pricing — know the exact cost before we start",
      "Same-day assessment and fast scheduling",
      "Clean water, reliable pressure, zero rust — guaranteed"
    ],
    faqs: [
      {
        q: "How long does a full home repipe take?",
        a: "Most single-family homes in Las Vegas can be repiped in 1-3 days depending on size and complexity. We'll give you a realistic timeline before starting."
      },
      {
        q: "Do I need to move out during the repipe?",
        a: "Typically no. Water is turned off during active work periods, but we schedule efficiently to minimize impact. Most families stay home throughout the process."
      },
      {
        q: "What pipe material do you use?",
        a: "We typically use PEX-A or copper depending on your home's needs, budget, and local code requirements. Both are modern, durable, and far superior to old galvanized or polybutylene pipe."
      },
      {
        q: "Is a repipe covered by homeowner's insurance?",
        a: "Coverage depends on your policy. Damage caused by pipe failure is often covered, but the repipe itself may not be. We can help with documentation and claims support."
      },
      {
        q: "How do I know if my home needs a full repipe vs. spot repair?",
        a: "If you're experiencing multiple leaks in different locations or have aging pipe materials, a repipe is usually more cost-effective long-term. We'll give you an honest recommendation."
      }
    ],
    relatedServices: [
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" },
      { name: "Mainline Replacement", slug: "mainline-replacement-las-vegas" },
      { name: "Slab Leak Repair", slug: "slab-leak-repair-las-vegas" }
    ],
    icon: "ri-settings-3-line"
  },
  {
    slug: "mainline-replacement-las-vegas",
    title: "Mainline Replacement in Las Vegas — Fast, Reliable Service",
    metaTitle: "Mainline Replacement Las Vegas — Licensed Plumbers | Leak Detectives",
    metaDescription: "Expert mainline replacement in Las Vegas. Fix failing water main lines before they cause major damage. Licensed & insured. Same-day response. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20plumbing%20crew%20performing%20main%20water%20line%20replacement%20excavation%20in%20a%20residential%20front%20yard%20Las%20Vegas%20neighborhood%2C%20safety%20cones%2C%20professional%20equipment%2C%20trench%20dig%20with%20new%20pipe%20visible%2C%20licensed%20plumbers%20in%20professional%20uniforms%20working%20in%20bright%20daylight%2C%20organized%20professional%20worksite&width=1920&height=700&seq=mainline-hero-001&orientation=landscape",
    tagline: "Your Main Line Is Failing — Don't Wait Until It Bursts",
    intro: [
      "The main water line is the most critical pipe in your home — it's the single line that brings all your fresh water from the city supply to your entire plumbing system. When it fails, everything stops. And in Las Vegas, shifting desert soil, temperature extremes, and aging infrastructure make mainline failures more common than most homeowners realize.",
      "At Leak Detectives, we handle mainline replacement throughout Las Vegas, Henderson, North Las Vegas, Summerlin, Spring Valley, and Paradise — efficiently and professionally, from detection and diagnosis through full replacement with modern pipe materials. We minimize excavation, protect your landscaping where possible, and restore everything when the job is done.",
      "Don't wait for a full burst. If you're experiencing low pressure, soggy yards, unexplained high water bills, or visible ground settling near your water meter, your main line may already be failing. Call us for a same-day assessment."
    ],
    internalLinkBlurbs: [
      {
        before: "If you're not sure whether it's the mainline or interior plumbing, our",
        linkText: "water leak detection service",
        slug: "water-leak-detection-las-vegas",
        after: "can isolate the source quickly using pressure testing and acoustic equipment."
      },
      {
        before: "Main line issues frequently accompany broader plumbing problems — many homeowners in North Las Vegas and Summerlin also schedule a",
        linkText: "full home repipe",
        slug: "home-repiping-las-vegas",
        after: "at the same time to address aging interior lines in one visit."
      },
      {
        before: "If your sewer line is also showing signs of trouble, see our",
        linkText: "sewer line repair service",
        slug: "sewer-line-repair-las-vegas",
        after: "— both lines often run parallel and can be addressed together to avoid repeat excavation."
      }
    ],
    signs: [
      "Significant drop in water pressure throughout the entire home",
      "Wet or soggy areas in your front yard near the water meter",
      "Unexplained spikes in your water bill",
      "Discolored water — rust or sediment — at all fixtures",
      "Sound of water rushing in the yard when no water is running indoors",
      "Ground settling or sinkholes forming near the main line path",
      "Frequent backflow issues or reduced flow at all fixtures simultaneously",
      "Main line is older than 30 years and has never been replaced"
    ],
    howWeFixIt: [
      {
        title: "Line Location & Assessment",
        description: "We locate your existing main line and assess its condition using pressure testing and camera inspection to determine the extent of the failure before recommending replacement."
      },
      {
        title: "Trenchless or Traditional Replacement",
        description: "When conditions allow, we use trenchless pipe bursting or pipe lining to replace your main line with minimal excavation — preserving your landscaping and driveway."
      },
      {
        title: "Full Excavation (When Required)",
        description: "For severe failures or difficult access, we excavate professionally, replace the full line with modern HDPE or PVC pipe, and backfill and compact the trench properly."
      },
      {
        title: "Connection & Pressure Test",
        description: "We reconnect to the city supply and your home plumbing, pressure test the new line to code, and restore water service before completing a final inspection."
      }
    ],
    benefits: [
      "Licensed & insured main line replacement specialists",
      "Trenchless options available — less disruption to your yard",
      "Modern HDPE or PVC materials built for Las Vegas soil conditions",
      "Flat-rate pricing with no surprise charges",
      "Same-day assessment — fast scheduling for urgent situations",
      "Restore full water pressure and clean water throughout your home"
    ],
    faqs: [
      {
        q: "How long does mainline replacement take?",
        a: "Most mainline replacements in Las Vegas are completed within one day. Longer runs or complex access situations may take two days. We'll give you a clear timeline upfront."
      },
      {
        q: "Can you replace my mainline without destroying my yard?",
        a: "In many cases, yes — trenchless pipe bursting allows us to replace the line with minimal digging. We'll assess your specific situation and recommend the least invasive approach."
      },
      {
        q: "How do I know if it's the mainline or a pipe inside my home?",
        a: "If low pressure or water issues affect your entire home simultaneously, it's likely the main line. We can confirm with a simple pressure test and inspection."
      },
      {
        q: "Does the city handle mainline repairs?",
        a: "The city typically owns the line up to the meter. Everything from the meter to your home is your responsibility. We handle that section completely."
      },
      {
        q: "What material do you use for mainline replacement?",
        a: "We use modern HDPE (high-density polyethylene) or PVC, both of which are highly durable, corrosion-resistant, and appropriate for Las Vegas soil and water conditions."
      }
    ],
    relatedServices: [
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" },
      { name: "Home Repiping", slug: "home-repiping-las-vegas" },
      { name: "Sewer Line Repair", slug: "sewer-line-repair-las-vegas" }
    ],
    icon: "ri-git-branch-line"
  },
  {
    slug: "slab-leak-repair-las-vegas",
    title: "Slab Leak Repair in Las Vegas — Fast, Non-Invasive Detection & Repair",
    metaTitle: "Slab Leak Repair Las Vegas — Same-Day Detection | Leak Detectives",
    metaDescription: "Professional slab leak detection & repair in Las Vegas. Non-invasive methods. Find the leak without destroying your floors. Licensed & insured. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20slab%20leak%20detection%20technician%20using%20acoustic%20listening%20device%20on%20concrete%20floor%20of%20a%20residential%20home%2C%20specialized%20equipment%20detecting%20underground%20pipe%20leak%20beneath%20foundation%20slab%2C%20modern%20residential%20interior%2C%20professional%20plumber%20in%20uniform%2C%20focused%20diagnostic%20work%2C%20electronic%20detection%20tools%20visible&width=1920&height=700&seq=slab-leak-hero-001&orientation=landscape",
    tagline: "Slab Leaks Destroy Foundations — Find It Before It's Too Late",
    intro: [
      "A slab leak is one of the most serious plumbing problems a Las Vegas homeowner can face. When a water line beneath your concrete foundation develops a leak, the water has nowhere to go — it erodes the soil, weakens the slab, and can cause thousands of dollars in structural damage before you even notice the first sign.",
      "Las Vegas homes are particularly vulnerable. The extreme heat causes pipes to expand and contract repeatedly, while the alkaline soil and hard water accelerate pipe corrosion. Homes in Henderson, Summerlin, North Las Vegas, Spring Valley, and Paradise built in the 70s, 80s, and 90s with copper plumbing are especially prone to slab leaks.",
      "At Leak Detectives, we use advanced electronic listening equipment and thermal imaging to pinpoint slab leaks without jackhammering your floors unnecessarily. We locate the problem accurately first — then recommend the most minimally invasive repair option, whether that's direct access, rerouting, or pipe lining."
    ],
    internalLinkBlurbs: [
      {
        before: "If you're unsure whether the problem is a slab leak or a supply line issue above ground, start with our",
        linkText: "professional water leak detection service",
        slug: "water-leak-detection-las-vegas",
        after: "— we'll use acoustic and thermal tools to isolate the exact source before any repair work begins."
      },
      {
        before: "Many Henderson and Summerlin homeowners with slab leaks in aging copper systems choose to combine the repair with",
        linkText: "whole-home repiping",
        slug: "home-repiping-las-vegas",
        after: "to eliminate all failing pipes at once rather than dealing with recurring leaks."
      },
      {
        before: "If a slab leak has caused standing water or moisture damage, our",
        linkText: "water damage restoration team",
        slug: "water-damage-restoration-las-vegas",
        after: "can deploy drying equipment immediately to prevent mold and structural deterioration."
      }
    ],
    signs: [
      "Hot or warm spots on your floor — especially on carpet or tile",
      "Sound of running water under your floor when all fixtures are off",
      "Sudden spike in your water bill without explanation",
      "Low water pressure throughout the home",
      "Cracks appearing in your walls, floors, or foundation",
      "Mold or mildew smell coming from floors or lower walls",
      "Wet baseboards or lifting floor material",
      "Your water heater runs constantly without explanation"
    ],
    howWeFixIt: [
      {
        title: "Slab Leak Detection",
        description: "We use electronic acoustic listening equipment calibrated for concrete environments to hear water escaping beneath your slab — pinpointing the exact location without guessing."
      },
      {
        title: "Thermal Imaging Confirmation",
        description: "Thermal cameras detect temperature differences caused by leaking water beneath the slab, giving us a clear map of the leak location and any secondary moisture spread."
      },
      {
        title: "Repair Options Assessment",
        description: "We explain all three repair options: direct access (jackhammer, fix, patch), pipe rerouting (bypass the slab entirely with new above-slab piping), and pipe lining (insert epoxy liner inside existing pipe)."
      },
      {
        title: "Repair & Restoration",
        description: "Whichever method is selected, we complete the repair to code, restore water service, and coordinate any concrete or flooring restoration needed."
      }
    ],
    benefits: [
      "Non-invasive detection — locate the leak before any concrete is touched",
      "Licensed & insured slab leak specialists serving Las Vegas",
      "Multiple repair options — we find the least invasive solution",
      "Same-day detection appointments available",
      "Flat-rate pricing — know the full cost before we start",
      "We work with your insurance company and provide full documentation"
    ],
    faqs: [
      {
        q: "How do I know if I have a slab leak?",
        a: "The most common signs are warm spots on your floor, the sound of running water when nothing is on, and a spike in your water bill. If you notice any of these, call us immediately."
      },
      {
        q: "Do you have to jackhammer my floor to fix a slab leak?",
        a: "Not always. We offer pipe rerouting and pipe lining as alternatives to direct slab access. We'll find the least invasive option for your specific situation."
      },
      {
        q: "How much does slab leak repair cost in Las Vegas?",
        a: "Cost varies based on the repair method chosen. We provide a flat-rate quote before any work begins. Call (725) 267-5482 for a free estimate."
      },
      {
        q: "Is slab leak repair covered by homeowner's insurance?",
        a: "Many policies cover water damage caused by slab leaks, and some cover the cost of accessing the leak. We provide all necessary documentation to support your claim."
      },
      {
        q: "How fast can you get here for a slab leak?",
        a: "We offer same-day service throughout Las Vegas. Slab leaks are urgent — call us and we'll get to you as fast as possible."
      }
    ],
    relatedServices: [
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" },
      { name: "Home Repiping", slug: "home-repiping-las-vegas" },
      { name: "Water Damage Restoration", slug: "water-damage-restoration-las-vegas" },
      { name: "Emergency Plumbing", slug: "emergency-plumbing-las-vegas" }
    ],
    icon: "ri-building-fill"
  },
  {
    slug: "emergency-plumbing-las-vegas",
    title: "Emergency Plumbing in Las Vegas — 24/7 Immediate Response",
    metaTitle: "Emergency Plumbing Las Vegas — 24/7 Same-Day Service | Leak Detectives",
    metaDescription: "24/7 emergency plumbing in Las Vegas. Burst pipes, major leaks, flooding — we respond fast. Licensed & insured. Call now: (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Emergency%20plumber%20arriving%20at%20residential%20home%20at%20night%20with%20professional%20truck%20and%20equipment%20for%20urgent%20pipe%20burst%20repair%2C%20dramatic%20lighting%2C%20urgency%20conveyed%20through%20quick%20professional%20response%2C%20licensed%20plumber%20with%20tool%20bag%20ready%20to%20work%2C%20Las%20Vegas%20suburban%20neighborhood%2C%20crisis%20situation%20being%20expertly%20handled%20by%20professional&width=1920&height=700&seq=emergency-hero-001&orientation=landscape",
    tagline: "Plumbing Emergency? Call Now — We Respond Fast, 24/7",
    intro: [
      "A plumbing emergency doesn't wait for business hours. A burst pipe at 2am, a flooding bathroom on a Sunday morning, or a failed water heater on Christmas Day — these situations require an immediate, professional response. Every minute you wait, the damage gets worse.",
      "Leak Detectives provides 24/7 emergency plumbing service throughout Las Vegas, Henderson, North Las Vegas, Summerlin, Spring Valley, and Paradise. When you call, you speak directly to a licensed plumber — not an answering service. We give you immediate guidance and dispatch a technician as fast as possible.",
      "From burst pipes and major water leaks to sewage backups and complete fixture failures, our emergency plumbing team handles it all. We arrive fully equipped, diagnose fast, and stop the damage before it destroys your property."
    ],
    internalLinkBlurbs: [
      {
        before: "If your emergency involves a leak beneath the foundation, our",
        linkText: "slab leak detection and repair team",
        slug: "slab-leak-repair-las-vegas",
        after: "is equipped to respond same-day and locate the source without unnecessary demolition."
      },
      {
        before: "Water damage spreads fast — if flooding has already occurred, our",
        linkText: "water damage restoration specialists",
        slug: "water-damage-restoration-las-vegas",
        after: "can deploy extraction and drying equipment immediately after the emergency is contained."
      },
      {
        before: "For sewer backups or sewage overflow emergencies, see our dedicated",
        linkText: "sewer line repair service",
        slug: "sewer-line-repair-las-vegas",
        after: "— we use camera inspection to diagnose the blockage or damage fast."
      }
    ],
    signs: [
      "Burst pipe causing active flooding in your home",
      "Sewage backup coming up through drains or toilets",
      "Complete loss of water pressure throughout the home",
      "Gas leak accompanied by plumbing symptoms",
      "Water heater failure causing no hot water or active leaking",
      "Overflowing toilet that won't stop despite shutoff",
      "Major leak visible under slab, behind walls, or from ceiling",
      "Frozen or burst pipe after temperature drop"
    ],
    howWeFixIt: [
      {
        title: "Immediate Damage Control",
        description: "When we arrive, our first priority is stopping the water. We locate and shut off the source, assess the extent of the damage, and prevent further loss to your property."
      },
      {
        title: "Rapid Diagnosis",
        description: "Using professional detection equipment, we identify the source of the emergency quickly — giving you a clear picture of what happened and what's needed to fix it."
      },
      {
        title: "Emergency Repair",
        description: "We carry the materials and equipment to handle most emergency repairs on the spot — burst pipes, failed fittings, broken shutoffs, and more — restoring service fast."
      },
      {
        title: "Documentation & Follow-Up",
        description: "We provide complete documentation of the emergency and repairs for your insurance claim, and schedule any follow-up work needed once the emergency is contained."
      }
    ],
    benefits: [
      "True 24/7 emergency response — no answering service, speak to a real plumber",
      "Same-day service available throughout Las Vegas and Clark County",
      "Licensed & insured emergency plumbing specialists",
      "We carry materials to fix most emergencies on the first visit",
      "Flat-rate emergency pricing — no price gouging during crisis",
      "Full insurance documentation provided"
    ],
    faqs: [
      {
        q: "What should I do while waiting for the emergency plumber?",
        a: "Shut off your main water valve immediately to stop the water. If it's a gas issue, leave the house and call the gas company first. Our technician will guide you over the phone."
      },
      {
        q: "Do you charge extra for after-hours emergency calls?",
        a: "We provide flat-rate pricing and are transparent about all costs before work begins, including for emergency calls. Call (725) 267-5482 and we'll give you an honest estimate."
      },
      {
        q: "How fast can you get to my home?",
        a: "We aim for the fastest possible response throughout Las Vegas. Call us now — we'll tell you exactly when to expect us."
      },
      {
        q: "Can you fix a burst pipe the same day?",
        a: "Yes — in most cases we carry the materials to repair burst pipes on the spot during the same visit."
      },
      {
        q: "Do you handle sewer and sewage emergencies?",
        a: "Yes — sewage backups are among the most urgent plumbing emergencies. Call us immediately if sewage is backing up into your home."
      }
    ],
    relatedServices: [
      { name: "Slab Leak Repair", slug: "slab-leak-repair-las-vegas" },
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" },
      { name: "Water Damage Restoration", slug: "water-damage-restoration-las-vegas" },
      { name: "Sewer Line Repair", slug: "sewer-line-repair-las-vegas" }
    ],
    icon: "ri-alarm-warning-fill"
  },
  {
    slug: "water-heater-installation-las-vegas",
    title: "Water Heater Installation in Las Vegas — Same-Day Service",
    metaTitle: "Water Heater Installation Las Vegas — Same-Day | Leak Detectives",
    metaDescription: "Professional water heater installation & replacement in Las Vegas. Tank and tankless options. Licensed & insured. Same-day service available. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20licensed%20plumber%20installing%20a%20modern%20water%20heater%20unit%20in%20a%20residential%20utility%20room%20in%20Las%20Vegas%2C%20new%20water%20heater%20connected%20with%20copper%20pipes%20and%20pressure%20relief%20valve%2C%20bright%20professional%20lighting%2C%20clean%20installation%20in%20progress%20with%20professional%20tools%20visible%2C%20licensed%20specialist%20checking%20connections&width=1920&height=700&seq=water-heater-hero-001&orientation=landscape",
    tagline: "No Hot Water? We Install Same-Day Throughout Las Vegas",
    intro: [
      "A failing water heater is more than an inconvenience — it's a daily disruption that affects your whole household. And when a tank fails completely, it can flood your utility room and cause serious water damage in minutes. Don't wait for a full failure.",
      "Leak Detectives installs and replaces water heaters throughout Las Vegas, Henderson, Summerlin, North Las Vegas, Spring Valley, and Paradise with same-day service. Whether you need a traditional tank water heater or want to upgrade to a more efficient tankless system, our licensed and insured plumbers handle the complete installation.",
      "We recommend the right system for your household size, water usage, and budget — not the most expensive option. Flat-rate pricing with no hidden fees, and we're upfront about every cost before we begin."
    ],
    internalLinkBlurbs: [
      {
        before: "If your water heater failure has caused flooding, our",
        linkText: "emergency plumbing team",
        slug: "emergency-plumbing-las-vegas",
        after: "can respond immediately to contain the situation and prevent further water damage."
      },
      {
        before: "Las Vegas hard water significantly shortens water heater lifespan — pairing your new installation with a",
        linkText: "whole-house water softener",
        slug: "water-filtration-softener-las-vegas",
        after: "can dramatically extend the life of your new unit and improve overall water quality."
      },
      {
        before: "If you're experiencing issues with other plumbing throughout the home, it may be time to consider",
        linkText: "full home repiping",
        slug: "home-repiping-las-vegas",
        after: "— many Henderson and Summerlin homeowners combine a water heater replacement with a complete pipe upgrade."
      }
    ],
    signs: [
      "No hot water or inconsistent water temperature",
      "Water heater is 10+ years old (tank) or 15+ years (tankless)",
      "Rust-colored or metallic-tasting hot water",
      "Popping, rumbling, or banging sounds from the water heater",
      "Visible rust or corrosion on the tank or connections",
      "Water pooling around the base of the unit",
      "Pilot light goes out frequently or won't stay lit",
      "Dramatically higher gas or electric bills without explanation"
    ],
    howWeFixIt: [
      {
        title: "Assessment & Recommendation",
        description: "We evaluate your current system, your household's hot water demands, and your utility setup to recommend the most cost-effective and appropriate replacement."
      },
      {
        title: "Safe Removal of Old Unit",
        description: "We disconnect and remove your existing water heater safely — including draining the tank, capping connections, and disposing of the old unit properly."
      },
      {
        title: "Professional Installation",
        description: "We install your new water heater to code — securing all connections, installing a new pressure relief valve, and ensuring proper venting for gas units."
      },
      {
        title: "Testing & Walkthrough",
        description: "We test the system fully before we leave, confirm it's producing hot water correctly, and walk you through the unit's operation and any maintenance you should know."
      }
    ],
    benefits: [
      "Tank and tankless water heater installation available",
      "Same-day installation throughout Las Vegas and Henderson",
      "Licensed & insured — all installations to Nevada code",
      "Flat-rate pricing with free estimate before any work begins",
      "Old unit removed and disposed of as part of the service",
      "Honest brand and model recommendations — no upselling"
    ],
    faqs: [
      {
        q: "How long does water heater installation take?",
        a: "Most standard water heater replacements take 2-3 hours from start to hot water. Tankless installations may take longer depending on gas line and venting modifications needed."
      },
      {
        q: "Should I get a tank or tankless water heater?",
        a: "Tankless heaters cost more upfront but can significantly reduce energy costs long-term. We'll help you compare options and make the right choice for your household and budget."
      },
      {
        q: "Do you remove and haul away my old water heater?",
        a: "Yes — removal and disposal of the old unit is included in our installation service. You won't have to deal with getting rid of it yourself."
      },
      {
        q: "How long does a water heater typically last?",
        a: "Tank water heaters typically last 8-12 years. Tankless units can last 15-20 years with proper maintenance. Las Vegas hard water can shorten lifespan if the unit isn't maintained."
      },
      {
        q: "Can you install a water heater the same day I call?",
        a: "In most cases, yes — we offer same-day water heater installation throughout Las Vegas. Call (725) 267-5482 early and we'll get you hot water back by end of day."
      }
    ],
    relatedServices: [
      { name: "Emergency Plumbing", slug: "emergency-plumbing-las-vegas" },
      { name: "Home Repiping", slug: "home-repiping-las-vegas" },
      { name: "Water Filtration / Softener", slug: "water-filtration-softener-las-vegas" }
    ],
    icon: "ri-fire-fill"
  },
  {
    slug: "water-filtration-softener-las-vegas",
    title: "Water Filtration & Softener Installation in Las Vegas",
    metaTitle: "Water Filtration & Softener Las Vegas — Clean Water | Leak Detectives",
    metaDescription: "Professional water filtration and softener installation in Las Vegas. Solve hard water problems. Licensed & insured. Same-day assessment. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20plumber%20installing%20a%20water%20softener%20system%20and%20whole-house%20water%20filtration%20unit%20in%20a%20clean%20residential%20utility%20room%20Las%20Vegas%20home%2C%20modern%20filtration%20equipment%20with%20clear%20tanks%20and%20control%20panel%2C%20bright%20professional%20lighting%2C%20licensed%20technician%20checking%20connections%20and%20settings%2C%20clean%20organized%20installation%20space&width=1920&height=700&seq=filtration-hero-001&orientation=landscape",
    tagline: "Las Vegas Hard Water Is Destroying Your Pipes — Fix It Today",
    intro: [
      "Las Vegas has some of the hardest water in the United States. The high mineral content — particularly calcium and magnesium — leaves scale buildup inside your pipes, on your fixtures, inside your water heater, and on every surface water touches. Over time, this scale restricts flow, destroys appliances, and dramatically shortens the life of your entire plumbing system.",
      "A properly installed whole-house water softener or filtration system doesn't just improve the taste and quality of your water — it protects every pipe, appliance, and fixture in your home from the damaging effects of Las Vegas hard water. Homeowners throughout Henderson, Summerlin, North Las Vegas, Spring Valley, and Paradise consistently see significant improvement in water quality and plumbing longevity after installation.",
      "Leak Detectives installs, services, and repairs water softeners and whole-house filtration systems throughout Las Vegas. Our licensed plumbers handle the complete installation, including connection to your main water supply and proper bypass valve installation."
    ],
    internalLinkBlurbs: [
      {
        before: "Hard water scale is one of the top reasons Las Vegas homeowners need early",
        linkText: "water heater replacement",
        slug: "water-heater-installation-las-vegas",
        after: "— a softener protects your new unit and significantly extends its service life."
      },
      {
        before: "Scale buildup also accelerates pipe corrosion — if your plumbing is already showing signs of damage, our team can assess whether",
        linkText: "home repiping",
        slug: "home-repiping-las-vegas",
        after: "is the right solution alongside your water treatment installation."
      },
      {
        before: "If you're noticing unexplained pressure drops or discolored water, get a",
        linkText: "professional water leak detection",
        slug: "water-leak-detection-las-vegas",
        after: "to rule out internal pipe damage before attributing everything to hard water scale."
      }
    ],
    signs: [
      "White or yellowish mineral scale on faucets, showerheads, and tile",
      "Soap doesn't lather well in the shower or when washing hands",
      "Spots on dishes and glassware after running through the dishwasher",
      "Skin feels dry or tight after showering",
      "Water heater requires frequent maintenance or has shorter-than-expected lifespan",
      "Reduced water flow due to scale buildup inside pipes",
      "Unpleasant taste or odor from tap water",
      "High water bills — scale reduces appliance efficiency"
    ],
    howWeFixIt: [
      {
        title: "Water Quality Assessment",
        description: "We test your water hardness level and identify any additional filtration needs — chlorine, sediment, or other contaminants — to recommend the right system for your home."
      },
      {
        title: "System Selection",
        description: "We help you select a correctly-sized water softener or filtration system based on your household size, water usage, and specific water quality issues — not just the most expensive option."
      },
      {
        title: "Professional Installation",
        description: "We connect the system to your main water supply line, install bypass valves, connect to drain for backwash, and set up the system for your specific water hardness level."
      },
      {
        title: "Setup & Training",
        description: "We test the system, confirm proper softening or filtration, and walk you through basic maintenance — salt refills, filter changes, and regeneration cycles."
      }
    ],
    benefits: [
      "Protects your entire plumbing system from hard water scale damage",
      "Licensed & insured installation throughout Las Vegas",
      "Whole-house softener and filtration options available",
      "Flat-rate pricing — no surprise charges",
      "Same-day assessment available",
      "Extended lifespan for water heaters, dishwashers, and washing machines"
    ],
    faqs: [
      {
        q: "How hard is Las Vegas water?",
        a: "Las Vegas water is consistently rated among the hardest in the US — typically 16-17 grains per gallon (GPG). This is considered 'very hard' and benefits significantly from a water softener."
      },
      {
        q: "What's the difference between a water softener and a water filter?",
        a: "A softener removes calcium and magnesium (hardness minerals) through ion exchange. A filter removes contaminants, chlorine, sediment, and taste/odor issues. Many homes benefit from both."
      },
      {
        q: "How long does installation take?",
        a: "Most water softener or filtration installations take 2-4 hours. More complex whole-house systems may take longer. We'll give you a clear timeline before starting."
      },
      {
        q: "Does a water softener affect drinking water?",
        a: "Softeners add a small amount of sodium to the water as part of the softening process. Many homeowners add an under-sink reverse osmosis filter for drinking water as a complement."
      },
      {
        q: "Do you service and maintain water softeners?",
        a: "Yes — we service and repair all major brands of water softeners and filtration systems throughout Las Vegas. Call us if your system isn't performing correctly."
      }
    ],
    relatedServices: [
      { name: "Water Heater Installation", slug: "water-heater-installation-las-vegas" },
      { name: "Home Repiping", slug: "home-repiping-las-vegas" },
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" }
    ],
    icon: "ri-filter-3-fill"
  },
  {
    slug: "garbage-disposal-repair-las-vegas",
    title: "Garbage Disposal Repair in Las Vegas — Same-Day Fix",
    metaTitle: "Garbage Disposal Repair Las Vegas — Fast Same-Day Service | Leak Detectives",
    metaDescription: "Garbage disposal repair and replacement in Las Vegas. Humming, jammed, leaking, or dead — we fix it fast. Licensed & insured. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20plumber%20repairing%20and%20replacing%20a%20kitchen%20garbage%20disposal%20unit%20under%20the%20sink%20in%20a%20modern%20Las%20Vegas%20home%20kitchen%2C%20bright%20kitchen%20lighting%2C%20licensed%20technician%20working%20on%20sink%20plumbing%20connections%2C%20disposal%20unit%20visible%20with%20tools%2C%20clean%20organized%20professional%20repair%20scene&width=1920&height=700&seq=disposal-hero-001&orientation=landscape",
    tagline: "Garbage Disposal Dead or Jammed? We Fix It Same Day",
    intro: [
      "A broken garbage disposal brings your kitchen to a halt. Whether it's jammed, humming but not spinning, leaking under the sink, or completely dead — a malfunctioning disposal is a mess-making inconvenience that most homeowners want fixed as fast as possible.",
      "Leak Detectives handles garbage disposal repair and replacement throughout Las Vegas, Henderson, North Las Vegas, Summerlin, Spring Valley, and Paradise with same-day service. Our licensed plumbers diagnose the issue fast — and in most cases, we can repair or replace your disposal on the same visit.",
      "We work on all major brands including InSinkErator, Moen, Waste King, and others. If repair isn't cost-effective, we'll recommend the right replacement model and install it completely — including new mounting hardware and drain connections."
    ],
    internalLinkBlurbs: [
      {
        before: "If your disposal leak has caused water damage under the sink or inside the cabinet, our",
        linkText: "water damage restoration team",
        slug: "water-damage-restoration-las-vegas",
        after: "can assess the extent of moisture damage and dry everything out properly."
      },
      {
        before: "If your kitchen drain is still slow after disposal repair, you may have a deeper blockage — see our",
        linkText: "professional drain cleaning service",
        slug: "drain-cleaning-las-vegas",
        after: "for a complete clearing of your kitchen drain line."
      },
      {
        before: "For disposal issues that are part of a larger kitchen plumbing failure, our",
        linkText: "emergency plumbing team",
        slug: "emergency-plumbing-las-vegas",
        after: "is available 24/7 to handle urgent situations throughout Las Vegas and Henderson."
      }
    ],
    signs: [
      "Disposal hums but doesn't grind (motor stuck or jammed)",
      "Disposal is completely dead — no sound at all when switched on",
      "Leaking from the bottom, side, or where it connects to the sink drain",
      "Extremely loud grinding, clanking, or rattling noise",
      "Disposal takes much longer than normal to process food waste",
      "Frequent resets required to keep the disposal working",
      "Drain backing up even with the disposal running",
      "Foul odor coming from the disposal even after cleaning"
    ],
    howWeFixIt: [
      {
        title: "Diagnosis",
        description: "We identify whether the issue is a jam, electrical failure, motor burnout, seal failure, or something else — giving you a clear diagnosis and options before any work begins."
      },
      {
        title: "Jam Clearing",
        description: "If your disposal is jammed, we safely clear the obstruction, check for damage to the grinding components, and restore normal operation without replacing the unit."
      },
      {
        title: "Seal & Leak Repair",
        description: "Leaking disposals are often caused by failed gaskets or loose mounting. We repair or replace the seals and re-secure all connections to eliminate the leak."
      },
      {
        title: "Disposal Replacement",
        description: "If the motor has failed or the unit is beyond repair, we remove the old disposal and install a new unit — completely connected and tested before we leave."
      }
    ],
    benefits: [
      "Same-day garbage disposal repair and replacement throughout Las Vegas",
      "Licensed & insured plumbers — all brands serviced",
      "Diagnose first — we only replace what actually needs replacing",
      "Flat-rate pricing with free estimate before work begins",
      "We carry common disposal models for same-day installation",
      "Drain and plumbing connections properly resealed after installation"
    ],
    faqs: [
      {
        q: "Why is my garbage disposal humming but not working?",
        a: "A humming disposal that won't spin is usually jammed. There's an object or food material preventing the grinding plate from rotating. This is usually fixable without replacement."
      },
      {
        q: "Should I repair or replace my garbage disposal?",
        a: "If the motor has failed or the unit is leaking from the bottom (motor seal failure), replacement is usually more cost-effective. We'll give you an honest assessment."
      },
      {
        q: "How long does garbage disposal replacement take?",
        a: "Most disposal replacements take under an hour. We bring the tools and usually carry common units so the job gets done in a single visit."
      },
      {
        q: "What brands of disposal do you install?",
        a: "We work with all major brands including InSinkErator, Moen, Waste King, and Kohler. We'll recommend the right unit for your budget and kitchen usage."
      },
      {
        q: "Can a garbage disposal cause my sink to back up?",
        a: "Yes — if the disposal drain connection is clogged or the P-trap is blocked, the sink will back up. We check and clear all drain connections during our service."
      }
    ],
    relatedServices: [
      { name: "Drain Cleaning", slug: "drain-cleaning-las-vegas" },
      { name: "Emergency Plumbing", slug: "emergency-plumbing-las-vegas" },
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" }
    ],
    icon: "ri-tools-fill"
  },
  {
    slug: "water-damage-restoration-las-vegas",
    title: "Water Damage Restoration in Las Vegas — Fast Response",
    metaTitle: "Water Damage Restoration Las Vegas — Same-Day | Leak Detectives",
    metaDescription: "Water damage restoration in Las Vegas. Fast response to floods, leaks & burst pipes. Licensed & insured. Call now before mold sets in: (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20water%20damage%20restoration%20team%20with%20industrial%20dehumidifiers%20and%20air%20movers%20working%20in%20a%20flooded%20residential%20room%20Las%20Vegas%20home%2C%20water%20extraction%20equipment%20deployed%2C%20professional%20restoration%20specialists%20in%20uniform%20assessing%20damage%20and%20setting%20up%20drying%20equipment%2C%20serious%20professional%20response%20to%20water%20emergency&width=1920&height=700&seq=water-damage-hero-001&orientation=landscape",
    tagline: "Water Damage Spreads Fast — Call Now Before Mold Sets In",
    intro: [
      "Water damage is a race against the clock. Within 24 hours of a leak, flood, or burst pipe, mold can begin to develop in wet materials. Within 48-72 hours, structural damage accelerates and remediation costs multiply. The faster you call, the less damage you face.",
      "Leak Detectives provides emergency water damage response and restoration services throughout Las Vegas, Henderson, North Las Vegas, Summerlin, Spring Valley, and Paradise. We arrive fast, extract standing water, deploy professional drying equipment, and assess the full extent of structural damage — stopping the problem before it becomes a catastrophe.",
      "From a leaking roof during a rare Las Vegas monsoon to a burst pipe that flooded your kitchen, our team handles the complete process — extraction, drying, structural assessment, and coordination with your insurance company."
    ],
    internalLinkBlurbs: [
      {
        before: "If the water damage originated from a plumbing emergency, our",
        linkText: "24/7 emergency plumbing team",
        slug: "emergency-plumbing-las-vegas",
        after: "can stop the source while the restoration process begins — both services can run simultaneously."
      },
      {
        before: "Water damage from beneath the floor often traces back to a slab leak — visit our",
        linkText: "slab leak repair page",
        slug: "slab-leak-repair-las-vegas",
        after: "to understand how under-slab pipe failures cause moisture damage that's often mistaken for a surface leak."
      },
      {
        before: "To prevent future incidents, consider having our team perform a",
        linkText: "professional water leak detection",
        slug: "water-leak-detection-las-vegas",
        after: "after restoration is complete — identifying any hidden leaks before they cause damage again."
      }
    ],
    signs: [
      "Standing water or significant moisture accumulation indoors",
      "Water stains or discoloration on walls, ceilings, or floors",
      "Soft, spongy, or buckling floors",
      "Peeling paint or wallpaper bubbling from moisture behind walls",
      "Musty or earthy odor indicating hidden moisture or early mold",
      "Visible mold growth on any surface",
      "Swollen or warped door frames and baseboards",
      "Recent burst pipe, appliance failure, or roof leak"
    ],
    howWeFixIt: [
      {
        title: "Emergency Water Extraction",
        description: "We deploy powerful water extraction equipment to remove all standing water from your home as fast as possible — stopping ongoing damage immediately."
      },
      {
        title: "Moisture Mapping",
        description: "Using moisture meters and thermal imaging, we map the full extent of water infiltration — including hidden moisture in walls, subfloors, and ceiling cavities."
      },
      {
        title: "Professional Drying & Dehumidification",
        description: "We set up industrial air movers and commercial dehumidifiers to dry your structure to safe moisture levels — preventing mold growth and structural deterioration."
      },
      {
        title: "Documentation & Insurance Support",
        description: "We document all damage with photos, moisture readings, and written reports — providing everything you need to file an insurance claim successfully."
      }
    ],
    benefits: [
      "Same-day emergency response throughout Las Vegas and Clark County",
      "Licensed & insured water damage professionals",
      "Industrial drying equipment — faster and more effective than consumer tools",
      "Complete insurance documentation provided",
      "Prevent mold before it starts — fast dry-out is critical",
      "Honest assessment of what needs to be repaired vs. replaced"
    ],
    faqs: [
      {
        q: "How fast can you respond to a water damage emergency?",
        a: "We aim for the fastest possible response throughout Las Vegas. Call us immediately — every hour matters in a water damage situation."
      },
      {
        q: "Will my homeowner's insurance cover water damage restoration?",
        a: "Most homeowner's policies cover sudden and accidental water damage. We provide complete documentation including photos, moisture readings, and written reports to support your claim."
      },
      {
        q: "How long does it take to dry out water damage?",
        a: "Most water damage situations require 3-5 days of professional drying to reach safe moisture levels. We monitor throughout the process and provide daily readings."
      },
      {
        q: "Can I handle water damage cleanup myself?",
        a: "Consumer fans and dehumidifiers are far less powerful than professional equipment and often leave hidden moisture that leads to mold. Professional drying is always recommended."
      },
      {
        q: "How quickly does mold start growing after water damage?",
        a: "Mold can begin developing within 24-48 hours in wet materials, especially in Las Vegas's warm climate. Fast response and proper drying are essential to prevent mold."
      }
    ],
    relatedServices: [
      { name: "Emergency Plumbing", slug: "emergency-plumbing-las-vegas" },
      { name: "Slab Leak Repair", slug: "slab-leak-repair-las-vegas" },
      { name: "Water Leak Detection", slug: "water-leak-detection-las-vegas" }
    ],
    icon: "ri-flood-fill"
  },
  {
    slug: "sewer-line-repair-las-vegas",
    title: "Sewer Line Repair in Las Vegas — Same-Day Diagnosis & Repair",
    metaTitle: "Sewer Line Repair Las Vegas — Licensed & Fast | Leak Detectives",
    metaDescription: "Expert sewer line repair in Las Vegas. Camera inspection, trenchless options. Licensed & insured. Same-day service available. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20sewer%20line%20inspection%20and%20repair%20crew%20with%20drain%20camera%20equipment%20and%20trenchless%20repair%20tools%20working%20on%20residential%20property%20Las%20Vegas%2C%20sewer%20camera%20monitor%20showing%20pipe%20interior%2C%20professional%20plumbing%20crew%20in%20uniform%2C%20organized%20worksite%20with%20technical%20equipment%2C%20daylight%20professional%20scene&width=1920&height=700&seq=sewer-hero-001&orientation=landscape",
    tagline: "Sewer Problems Don't Fix Themselves — Get a Camera Inspection Today",
    intro: [
      "A sewer line problem is one of the most unpleasant and potentially expensive plumbing issues a homeowner can face. Whether it's a crack, bellied section, root intrusion, or complete collapse, a failing sewer line can back sewage into your home and create serious health and property hazards.",
      "Leak Detectives uses professional sewer camera inspection equipment to diagnose sewer line problems accurately throughout Las Vegas, Henderson, North Las Vegas, Summerlin, Spring Valley, and Paradise — without guessing and without unnecessary excavation. We show you exactly what's happening inside your sewer line, explain all your repair options, and get to work fast.",
      "We offer trenchless sewer repair options including pipe lining and pipe bursting for eligible situations — replacing your sewer line without destroying your yard, driveway, or landscaping. Where trenchless isn't possible, we complete traditional repairs professionally and efficiently."
    ],
    internalLinkBlurbs: [
      {
        before: "For recurring or severe sewer blockages that can't be cleared by snaking, our",
        linkText: "professional drain cleaning service",
        slug: "drain-cleaning-las-vegas",
        after: "uses hydro-jetting to completely scour the line — often as a first step before camera inspection."
      },
      {
        before: "If your sewer line runs parallel to your main water supply line, see our",
        linkText: "mainline replacement service",
        slug: "mainline-replacement-las-vegas",
        after: "— homeowners in Henderson and Paradise often address both lines at the same time to avoid repeat excavation."
      },
      {
        before: "A sewage backup constitutes a plumbing emergency — if it's happening right now, call our",
        linkText: "24/7 emergency plumbing team",
        slug: "emergency-plumbing-las-vegas",
        after: "for immediate response anywhere in the Las Vegas metro area."
      }
    ],
    signs: [
      "Multiple drains backing up simultaneously throughout the home",
      "Gurgling sounds from toilets or drains when water is used elsewhere",
      "Sewage odor inside the home or in the yard",
      "Soggy patches in the yard with lush, unusually green grass (sewage fertilizing lawn)",
      "Slow drains throughout the home that don't improve with snaking",
      "Toilet water level rises when you run the sink",
      "Rodent or insect activity increasing (they enter through cracked sewer lines)",
      "Foundation cracks developing near sewer line path"
    ],
    howWeFixIt: [
      {
        title: "Camera Inspection",
        description: "We run a high-definition camera through your sewer line to visually inspect the interior — identifying cracks, root intrusion, bellied sections, or collapse points precisely."
      },
      {
        title: "Hydro-Jetting (When Needed)",
        description: "For severe buildup or root intrusion, we use hydro-jetting to clear the line completely before assessment or repair — restoring full flow capacity."
      },
      {
        title: "Pipe Lining (Trenchless)",
        description: "For cracked or deteriorated but structurally intact pipes, we insert an epoxy liner that cures in place — creating a new pipe within the old one without excavation."
      },
      {
        title: "Pipe Bursting or Excavation",
        description: "For collapsed or severely damaged sections, pipe bursting fractures the old pipe while pulling a new one in place, or we excavate and replace traditionally where needed."
      }
    ],
    benefits: [
      "Camera inspection so you see exactly what's happening — no guessing",
      "Trenchless repair options available — preserve your yard and driveway",
      "Licensed & insured sewer line specialists in Las Vegas",
      "Same-day inspection and emergency service available",
      "Flat-rate pricing — know the cost before we start any work",
      "We coordinate with the city on permits and inspections"
    ],
    faqs: [
      {
        q: "How do I know if my sewer line is the problem?",
        a: "If multiple drains are backing up at once, you hear gurgling from toilets, or you smell sewage — it's very likely a main sewer line issue. A camera inspection confirms it definitively."
      },
      {
        q: "Can you repair my sewer line without digging up my yard?",
        a: "In many cases, yes. Trenchless pipe lining and pipe bursting allow us to repair or replace sewer lines with minimal excavation. Your yard, driveway, and landscaping may be fully preserved."
      },
      {
        q: "How long does sewer line repair take?",
        a: "Simple repairs can be completed in a day. More extensive replacement may take 1-3 days. We'll give you a realistic timeline after the camera inspection."
      },
      {
        q: "Do I need a permit for sewer line replacement?",
        a: "In most cases, yes. We handle all permitting with the city of Las Vegas or Clark County as part of our service."
      },
      {
        q: "Is sewer line repair covered by insurance?",
        a: "Basic homeowner's policies often don't cover sewer lines. However, many insurers offer sewer line coverage as an add-on. Check your policy — we can provide documentation regardless."
      }
    ],
    relatedServices: [
      { name: "Drain Cleaning", slug: "drain-cleaning-las-vegas" },
      { name: "Mainline Replacement", slug: "mainline-replacement-las-vegas" },
      { name: "Emergency Plumbing", slug: "emergency-plumbing-las-vegas" }
    ],
    icon: "ri-arrow-down-circle-fill"
  },
  {
    slug: "drain-cleaning-las-vegas",
    title: "Drain Cleaning in Las Vegas — Fast, Professional Service",
    metaTitle: "Drain Cleaning Las Vegas — Same-Day Clog Clearing | Leak Detectives",
    metaDescription: "Professional drain cleaning in Las Vegas. Clear clogs fast — kitchen, bathroom, main line. Licensed & insured. Same-day service. Call (725) 267-5482.",
    heroImageUrl: "https://readdy.ai/api/search-image?query=Professional%20plumber%20using%20electric%20drain%20snake%20and%20hydro-jetting%20equipment%20to%20clear%20a%20clogged%20drain%20in%20a%20residential%20Las%20Vegas%20home%2C%20modern%20bathroom%20or%20kitchen%20setting%2C%20licensed%20technician%20in%20professional%20uniform%20working%20on%20drain%20cleaning%20service%2C%20professional%20tools%20organized%20on%20worksite%2C%20clean%20professional%20scene&width=1920&height=700&seq=drain-hero-001&orientation=landscape",
    tagline: "Clogged Drains? We Clear Them Fast — Same Day Throughout Las Vegas",
    intro: [
      "A clogged drain is one of the most common plumbing problems — and one of the most annoying. Whether it's a kitchen sink full of standing water, a slow shower drain, or a backed-up toilet, a blocked drain disrupts your day and can quickly escalate to a bigger problem if not addressed properly.",
      "Leak Detectives provides professional drain cleaning services throughout Las Vegas, Henderson, North Las Vegas, Summerlin, Spring Valley, and Paradise with same-day availability. Unlike chemical drain cleaners that often make the problem worse over time, we use professional-grade electric snake machines and hydro-jetting equipment to completely clear the obstruction — not just punch through it temporarily.",
      "We also use camera inspection when needed to diagnose the root cause of persistent or recurring clogs — because sometimes a slow drain is a symptom of a bigger problem further down the line."
    ],
    internalLinkBlurbs: [
      {
        before: "When multiple drains back up at once, the problem is usually in the main sewer line — read about our",
        linkText: "sewer line repair and inspection service",
        slug: "sewer-line-repair-las-vegas",
        after: "to understand how camera inspection quickly identifies the root cause."
      },
      {
        before: "If your kitchen drain issues are connected to a garbage disposal problem, our",
        linkText: "garbage disposal repair team",
        slug: "garbage-disposal-repair-las-vegas",
        after: "can address both in a single visit — we check disposal connections and drain lines together."
      },
      {
        before: "A severe drain emergency — flooding, sewage overflow, or complete main line backup — warrants a call to our",
        linkText: "24/7 emergency plumbing service",
        slug: "emergency-plumbing-las-vegas",
        after: "for same-day urgent response throughout Henderson, Summerlin, and the greater Las Vegas area."
      }
    ],
    signs: [
      "Standing water in kitchen sink, shower, or bathtub",
      "Gurgling sounds from drains after water goes down",
      "Drain is slow even after using store-bought drain cleaner",
      "Foul odors coming from kitchen or bathroom drains",
      "Water backs up into other fixtures when draining",
      "Toilet requires multiple flushes or doesn't clear completely",
      "Multiple slow drains throughout the home (sign of main line issue)",
      "Recurring clogs in the same drain despite clearing attempts"
    ],
    howWeFixIt: [
      {
        title: "Diagnosis",
        description: "We identify the type, location, and severity of the clog before selecting the right clearing method — ensuring we solve the actual problem rather than a temporary symptom."
      },
      {
        title: "Electric Snake / Auger",
        description: "For most kitchen and bathroom clogs, a professional electric snake reaches deep into the drain to break up and remove the obstruction completely — not just punch a hole through it."
      },
      {
        title: "Hydro-Jetting",
        description: "For severe or recurring clogs, grease buildup, or scale accumulation, hydro-jetting uses high-pressure water to scour the interior of the pipe — leaving it clean end to end."
      },
      {
        title: "Camera Inspection",
        description: "For persistent or unexplained recurring clogs, we run a camera to identify root intrusion, pipe damage, or buildup deeper in the drain system — addressing the real cause."
      }
    ],
    benefits: [
      "Same-day drain cleaning throughout Las Vegas and Henderson",
      "Professional equipment — electric snake, hydro-jet, and camera available",
      "Licensed & insured — we clear drains properly, not temporarily",
      "Flat-rate pricing with no hourly surprises",
      "We identify root causes — not just symptoms",
      "Kitchen, bathroom, floor drain, and main sewer line clearing"
    ],
    faqs: [
      {
        q: "Why shouldn't I just use Drano or a chemical drain cleaner?",
        a: "Chemical drain cleaners often only partially clear clogs, leave residue, and can damage older pipes over time. They're also dangerous to use. Professional snaking completely removes the obstruction."
      },
      {
        q: "How long does drain cleaning take?",
        a: "Most single-drain clogs are cleared in 30-60 minutes. Main line clearing or hydro-jetting takes longer. We'll give you a realistic estimate when we diagnose the issue."
      },
      {
        q: "Why does my drain keep clogging in the same spot?",
        a: "Recurring clogs usually indicate a buildup issue (grease, hair, scale), a pipe bellying (low spot trapping debris), or root intrusion. A camera inspection can find the cause permanently."
      },
      {
        q: "Can you clear a main sewer line clog?",
        a: "Yes — main line clogs are one of our specialties. If multiple drains are slow or backing up simultaneously, call us for a same-day main line clearing."
      },
      {
        q: "Do you offer drain cleaning as a preventative service?",
        a: "Absolutely. Annual or bi-annual professional drain cleaning is a great way to prevent major clogs and keep your plumbing flowing freely — especially with Las Vegas hard water scale."
      }
    ],
    relatedServices: [
      { name: "Sewer Line Repair", slug: "sewer-line-repair-las-vegas" },
      { name: "Garbage Disposal Repair", slug: "garbage-disposal-repair-las-vegas" },
      { name: "Emergency Plumbing", slug: "emergency-plumbing-las-vegas" }
    ],
    icon: "ri-refresh-line"
  }
];

export const getServiceBySlug = (slug: string): ServicePageData | undefined =>
  servicePages.find((s) => s.slug === slug);
